import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Grid,
  Divider,
  Chip,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Autocomplete,
  Stack,
  Card,
  CardContent,
  Fade,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  ExpandMore,
  Save,
  Refresh,
  Storage,
  CheckCircle,
  Edit,
  Info,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../../contexts/ApiContext';

interface FieldMetadata {
  field: string;
  label: string;
  type: 'string' | 'number' | 'boolean' | 'select' | 'multiselect' | 'json' | 'datetime' | 'user-reference';
  editable?: boolean;
  required?: boolean;
  options?: string[] | { value: string; label: string }[];
  placeholder?: string;
  helperText?: string;
  min?: number;
  max?: number;
  rows?: number;
  section?: string;
  tags?: string[];
  order?: number;
  description?: string;
}

interface EntityEditorMetadata {
  displayName: string;
  singularName: string;
  fields: FieldMetadata[];
}

interface GenericEntityEditorProps {
  entityType: string;
  entityId: string;
  onSaveSuccess?: () => void;
}

const GenericEntityEditor: React.FC<GenericEntityEditorProps> = ({
  entityType,
  entityId,
  onSaveSuccess,
}) => {
  const api = useApi();
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [hasChanges, setHasChanges] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [selectedUsers, setSelectedUsers] = useState<Record<string, any>>({});

  // Fetch entity data
  const { data: entityData, isLoading: entityLoading, error: entityError, refetch } = useQuery({
    queryKey: ['entity', entityType, entityId],
    queryFn: async () => {
      const response = await api.get(`/database/${entityType}/${entityId}/raw`);
      return response;
    },
  });

  // Fetch edit metadata
  const { data: metadata, isLoading: metadataLoading } = useQuery<EntityEditorMetadata>({
    queryKey: ['entity-edit-metadata', entityType],
    queryFn: async () => {
      const response = await api.get(`/entities/${entityType}/edit-metadata`);
      return response;
    },
  });

  // Fetch users for user-reference fields
  const { data: users = [] } = useQuery({
    queryKey: ['users-search', userSearchQuery],
    queryFn: async () => {
      const queryParams = new URLSearchParams();
      if (userSearchQuery) queryParams.append('search', userSearchQuery);
      queryParams.append('limit', '20');
      const response = await api.get(`/users?${queryParams.toString()}`);
      return response.users || [];
    },
    enabled: metadata?.fields.some(f => f.type === 'user-reference') || false,
  });

  // Initialize form data when entity data loads
  useEffect(() => {
    if (entityData && metadata) {
      const initialData: Record<string, any> = {};
      // The API returns data in { dynamo: {...}, cognito: {...} } structure
      const sourceData = entityData.dynamo || entityData;

      metadata.fields.forEach((field) => {
        initialData[field.field] = sourceData[field.field] ?? '';
      });
      setFormData(initialData);
      setHasChanges(false);
    }
  }, [entityData, metadata]);

  // Fetch and populate user-reference fields when data loads
  useEffect(() => {
    const fetchUserReferences = async () => {
      if (!entityData || !metadata) return;

      const sourceData = entityData.dynamo || entityData;
      const userReferenceFields = metadata.fields.filter(f => f.type === 'user-reference');

      if (userReferenceFields.length === 0) return;

      const newSelectedUsers: Record<string, any> = {};

      for (const field of userReferenceFields) {
        const userId = sourceData[field.field];
        if (userId) {
          try {
            // Fetch user details
            const userResponse = await api.get(`/users/${userId}`);
            if (userResponse) {
              newSelectedUsers[field.field] = {
                id: userId,
                email: userResponse.email || userResponse.user?.email || 'Unknown',
              };
            }
          } catch (error) {
            console.error(`Failed to fetch user ${userId}:`, error);
          }
        }
      }

      setSelectedUsers(newSelectedUsers);
    };

    fetchUserReferences();
  }, [entityData, metadata, api]);

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await api.put(`/database/${entityType}/${entityId}/raw`, data);
      return response;
    },
    onSuccess: () => {
      setSuccessMessage('Changes saved successfully!');
      setErrorMessage(null);
      setHasChanges(false);
      queryClient.invalidateQueries({ queryKey: ['entity', entityType, entityId] });
      queryClient.invalidateQueries({ queryKey: ['entities', entityType] });
      
      if (onSaveSuccess) {
        onSaveSuccess();
      }

      setTimeout(() => setSuccessMessage(null), 3000);
    },
    onError: (error: any) => {
      setErrorMessage(error.message || 'Failed to save changes');
      setSuccessMessage(null);
    },
  });

  const handleFieldChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setHasChanges(true);
    setSuccessMessage(null);
    setErrorMessage(null);
  };

  const handleSave = () => {
    // Process form data before submission
    const processedData: Record<string, any> = { ...formData };

    metadata?.fields.forEach((field) => {
      let value = processedData[field.field];

      // Convert JSON strings to objects
      if (field.type === 'json' && typeof value === 'string') {
        try {
          value = value.trim() === '' ? {} : JSON.parse(value);
        } catch (e) {
          value = {};
        }
      }

      // Convert number strings to numbers
      if (field.type === 'number' && value !== '' && value !== null) {
        value = Number(value);
      }

      processedData[field.field] = value;
    });

    // Send data in the format expected by the API
    // For non-user entities, wrap in dynamoUpdates
    const payload = entityType === 'user'
      ? { cognitoUpdates: {}, dynamoUpdates: processedData }
      : { dynamoUpdates: processedData };

    updateMutation.mutate(payload);
  };

  const handleRefresh = () => {
    refetch();
    setHasChanges(false);
    setSuccessMessage(null);
    setErrorMessage(null);
  };

  const renderField = (field: FieldMetadata) => {
    const value = formData[field.field] ?? '';
    const isEditable = field.editable !== false;

    // Don't render PK, SK, GSI keys, or timestamps
    if (['PK', 'SK', 'GSI1PK', 'GSI1SK', 'GSI2PK', 'GSI2SK', 'createdAt', 'updatedAt'].includes(field.field)) {
      return null;
    }

    switch (field.type) {
      case 'user-reference':
        const selectedUser = selectedUsers[field.field];
        return (
          <Autocomplete
            size="small"
            options={users}
            getOptionLabel={(option: any) => option.email || ''}
            value={selectedUser || null}
            onChange={(event, newValue) => {
              if (newValue) {
                setSelectedUsers({ ...selectedUsers, [field.field]: newValue });
                handleFieldChange(field.field, newValue.id);
              } else {
                setSelectedUsers({ ...selectedUsers, [field.field]: null });
                handleFieldChange(field.field, '');
              }
            }}
            onInputChange={(event, newInputValue) => {
              setUserSearchQuery(newInputValue);
            }}
            isOptionEqualToValue={(option: any, value: any) => {
              return option.id === value.id;
            }}
            disabled={!isEditable}
            renderInput={(params) => (
              <TextField
                {...params}
                label={field.label}
                required={field.required}
                helperText={field.helperText || field.description}
                placeholder={field.placeholder}
              />
            )}
            fullWidth
          />
        );

      case 'boolean':
        return (
          <FormControlLabel
            control={
              <Switch
                checked={Boolean(value)}
                onChange={(e) => handleFieldChange(field.field, e.target.checked)}
                disabled={!isEditable}
              />
            }
            label={field.label}
          />
        );

      case 'select':
        return (
          <FormControl fullWidth size="small" disabled={!isEditable}>
            <InputLabel>{field.label}</InputLabel>
            <Select
              value={value}
              onChange={(e) => handleFieldChange(field.field, e.target.value)}
              label={field.label}
            >
              {field.options?.map((option) => {
                const optionValue = typeof option === 'string' ? option : option.value;
                const optionLabel = typeof option === 'string' ? option : option.label;
                return (
                  <MenuItem key={optionValue} value={optionValue}>
                    {optionLabel}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        );

      case 'multiselect':
        return (
          <FormControl fullWidth disabled={!isEditable}>
            <Autocomplete
              multiple
              size="small"
              options={field.options as string[] || []}
              value={Array.isArray(value) ? value : []}
              onChange={(event, newValue) => handleFieldChange(field.field, newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={field.label}
                  helperText={field.helperText || field.description}
                />
              )}
              renderTags={(value: string[], getTagProps) =>
                value.map((option: string, index: number) => (
                  <Chip label={option} {...getTagProps({ index })} size="small" />
                ))
              }
            />
          </FormControl>
        );

      case 'json':
        return (
          <TextField
            fullWidth
            size="small"
            multiline
            rows={field.rows || 4}
            label={field.label}
            value={typeof value === 'object' ? JSON.stringify(value, null, 2) : value}
            onChange={(e) => handleFieldChange(field.field, e.target.value)}
            placeholder={field.placeholder}
            helperText={field.helperText || field.description}
            disabled={!isEditable}
            sx={{
              '& .MuiInputBase-root': {
                fontFamily: 'monospace',
                fontSize: '0.8rem',
                backgroundColor: 'grey.50',
                borderRadius: 1,
              },
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: 'primary.main',
                },
              },
            }}
          />
        );

      case 'number':
        return (
          <TextField
            fullWidth
            size="small"
            type="number"
            label={field.label}
            value={value}
            onChange={(e) => handleFieldChange(field.field, e.target.value)}
            placeholder={field.placeholder}
            helperText={field.helperText || field.description}
            disabled={!isEditable}
            required={field.required}
            inputProps={{
              min: field.min,
              max: field.max,
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: 'primary.main',
                },
              },
            }}
          />
        );

      default: // string, datetime
        return (
          <TextField
            fullWidth
            size="small"
            type={field.type === 'datetime' ? 'datetime-local' : 'text'}
            label={field.label}
            value={value}
            onChange={(e) => handleFieldChange(field.field, e.target.value)}
            placeholder={field.placeholder}
            helperText={field.helperText || field.description}
            disabled={!isEditable}
            required={field.required}
            sx={{
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: 'primary.main',
                },
              },
            }}
          />
        );
    }
  };

  // Group fields by section - must be called before any conditional returns
  const groupedFields = React.useMemo(() => {
    if (!metadata) return {};

    const groups: Record<string, FieldMetadata[]> = {};

    metadata.fields.forEach((field) => {
      // Skip internal fields
      if (['PK', 'SK', 'GSI1PK', 'GSI1SK', 'GSI2PK', 'GSI2SK', 'createdAt', 'updatedAt'].includes(field.field)) {
        return;
      }

      const section = field.section || 'General';
      if (!groups[section]) {
        groups[section] = [];
      }
      groups[section].push(field);
    });

    // Sort fields within each section by order
    Object.keys(groups).forEach((section) => {
      groups[section].sort((a, b) => (a.order || 0) - (b.order || 0));
    });

    return groups;
  }, [metadata]);

  const sectionOrder = Object.keys(groupedFields);

  if (entityLoading || metadataLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  if (entityError || !entityData || !metadata) {
    return (
      <Alert severity="error">
        Failed to load entity data. {entityError instanceof Error ? entityError.message : 'Unknown error'}
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 0 }}>
      {/* Compact Header Section */}
      <Card
        elevation={0}
        sx={{
          mb: 2,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          borderRadius: 1.5,
        }}
      >
        <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Box
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  borderRadius: '8px',
                  p: 0.75,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Edit sx={{ fontSize: 20 }} />
              </Box>
              <Box>
                <Typography variant="subtitle1" fontWeight={600} sx={{ lineHeight: 1.2 }}>
                  Advanced Database Editor
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                  Edit all fields for this {metadata?.singularName || entityType}
                </Typography>
              </Box>
            </Stack>
            <Stack direction="row" spacing={1}>
              <Tooltip title="Discard changes and reload data">
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Refresh sx={{ fontSize: 18 }} />}
                  onClick={handleRefresh}
                  disabled={updateMutation.isPending}
                  sx={{
                    color: 'white',
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                    py: 0.5,
                    px: 1.5,
                    '&:hover': {
                      borderColor: 'white',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    },
                  }}
                >
                  Refresh
                </Button>
              </Tooltip>
              <Tooltip title={hasChanges ? 'Save your changes' : 'No changes to save'}>
                <span>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={updateMutation.isPending ? <CircularProgress size={16} color="inherit" /> : <Save sx={{ fontSize: 18 }} />}
                    onClick={handleSave}
                    disabled={!hasChanges || updateMutation.isPending}
                    sx={{
                      backgroundColor: 'white',
                      color: '#667eea',
                      fontWeight: 600,
                      py: 0.5,
                      px: 1.5,
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      },
                      '&.Mui-disabled': {
                        backgroundColor: 'rgba(255, 255, 255, 0.3)',
                        color: 'rgba(255, 255, 255, 0.5)',
                      },
                    }}
                  >
                    {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
                  </Button>
                </span>
              </Tooltip>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {/* Compact Status Messages */}
      <Fade in={!!successMessage}>
        <Box mb={1.5}>
          {successMessage && (
            <Alert
              severity="success"
              icon={<CheckCircle sx={{ fontSize: 20 }} />}
              onClose={() => setSuccessMessage(null)}
              sx={{
                borderRadius: 1.5,
                py: 0.5,
                '& .MuiAlert-message': { py: 0.5 },
              }}
            >
              <Typography variant="body2" fontWeight={500}>{successMessage}</Typography>
            </Alert>
          )}
        </Box>
      </Fade>

      <Fade in={!!errorMessage}>
        <Box mb={1.5}>
          {errorMessage && (
            <Alert
              severity="error"
              onClose={() => setErrorMessage(null)}
              sx={{
                borderRadius: 1.5,
                py: 0.5,
                '& .MuiAlert-message': { py: 0.5 },
              }}
            >
              <Typography variant="body2" fontWeight={500}>{errorMessage}</Typography>
            </Alert>
          )}
        </Box>
      </Fade>

      <Fade in={hasChanges}>
        <Box mb={1.5}>
          {hasChanges && (
            <Alert
              severity="warning"
              icon={<Info sx={{ fontSize: 20 }} />}
              sx={{
                borderRadius: 1.5,
                py: 0.5,
                '& .MuiAlert-message': { py: 0.5 },
              }}
            >
              <Typography variant="body2" fontWeight={500}>
                You have unsaved changes. Click "Save Changes" to apply them.
              </Typography>
            </Alert>
          )}
        </Box>
      </Fade>

      {/* Compact Field Sections */}
      <Stack spacing={1.5}>
        {sectionOrder.map((sectionName, index) => (
          <Accordion
            key={sectionName}
            defaultExpanded={index === 0}
            elevation={0}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: '8px !important',
              '&:before': { display: 'none' },
              overflow: 'hidden',
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={{
                backgroundColor: 'background.default',
                borderBottom: '1px solid',
                borderColor: 'divider',
                minHeight: 48,
                '&.Mui-expanded': {
                  minHeight: 48,
                },
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
                '& .MuiAccordionSummary-content': {
                  my: 1,
                  '&.Mui-expanded': {
                    my: 1,
                  },
                },
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    backgroundColor: 'primary.main',
                  }}
                />
                <Typography variant="subtitle1" fontWeight={600} color="text.primary">
                  {sectionName}
                </Typography>
                <Chip
                  label={`${groupedFields[sectionName].length}`}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.7rem',
                    fontWeight: 500,
                    '& .MuiChip-label': { px: 1 },
                  }}
                />
              </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 2, backgroundColor: 'background.paper' }}>
              <Grid container spacing={2}>
                {groupedFields[sectionName].map((field) => (
                  <Grid
                    item
                    xs={12}
                    md={field.type === 'json' ? 12 : 6}
                    key={field.field}
                  >
                    {renderField(field)}
                  </Grid>
                ))}
              </Grid>
            </AccordionDetails>
          </Accordion>
        ))}
      </Stack>
    </Box>
  );
};

export default GenericEntityEditor;

